---
title: Authentication and Access
slug: authentication_and_access
order: 1
---

# Authentication and Access

The Norscom Automation Platform provides secure access through multiple authentication methods, ensuring your workflows and data remain protected while maintaining ease of use for authorized users.

## How to Log In

### 🔑 **Username and Password Login**

1. **Navigate to the Login Page**
   - Open your web browser and go to the platform URL
   - You'll see the login screen with username and password fields

2. **Enter Your Credentials**
   - **Username**: Enter your assigned username
   - **Password**: Type your secure password
   - Click the **"Log In"** button

3. **Access Granted**
   - Upon successful authentication, you'll be redirected to your dashboard
   - Your session will remain active until you log out or the session expires

### 🔐 **Single Sign-On (SSO) Login**

If your organization has configured SSO integration:

1. **Click the SSO Button**
   - Look for the **"Login with SSO"** or **"Login with Azure AD"** button
   - Click this button instead of entering username/password

2. **Complete SSO Authentication**
   - You'll be redirected to your organization's identity provider
   - Complete the authentication process (may include MFA if enabled)
   - You'll be automatically redirected back to the platform

3. **Automatic Access**
   - The platform will automatically log you in using your SSO credentials
   - You'll have access to all resources based on your assigned permissions

## What Happens After Login?

Once you're successfully authenticated:

- **Dashboard Access**: You'll see your personalized dashboard with relevant workflows and metrics
- **Menu Navigation**: Access to features and modules based on your user role and permissions
- **Session Management**: Your login session is maintained across browser tabs
- **Security**: All your actions are logged for audit purposes

## Security Features

- **Session Timeout**: Automatic logout after periods of inactivity
- **Role-Based Access**: You'll only see features and data you're authorized to access
- **Audit Logging**: All login attempts and user actions are recorded
- **Multi-Zone Support**: Access to different environments based on your permissions

## Need Help?

If you experience login issues:

1. **Check Credentials**: Ensure your username and password are correct
2. **Contact Support**: Reach out to your system administrator
3. **Password Reset**: Use the password reset function if available
4. **Browser Issues**: Try clearing cookies or using a different browser

---

*Note: SSO setup and advanced authentication configuration will be covered in the detailed setup documentation.*

